import React, { useState, useEffect } from 'react';
import CategorySelect from './CategorySelect';

interface FloatingEditorProps {
  feature: GeoJSON.Feature | null;
  onPropertyChange: (fieldPath: string, newValue: any) => void;
  onPropertyDelete: (fieldPath: string) => void;
  onSaveChanges: () => void;
  isOpen: boolean;
  onClose: () => void;
  readonly?: boolean;
  isSaving?: boolean;
  pendingChangesCount?: number;
}

const FloatingEditor: React.FC<FloatingEditorProps> = ({
  feature,
  onPropertyChange,
  onPropertyDelete,
  onSaveChanges,
  isOpen,
  onClose,
  readonly = false,
  isSaving = false,
  pendingChangesCount = 0,
}) => {
  const [editingProperties, setEditingProperties] = useState<any>({});
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [newPropertyKey, setNewPropertyKey] = useState('');
  const [newPropertyValue, setNewPropertyValue] = useState('');

  // 當feature改變時，重置編輯狀態
  useEffect(() => {
    if (feature?.properties) {
      setEditingProperties({ ...feature.properties });
    } else {
      setEditingProperties({});
    }
    setShowAddProperty(false);
    setNewPropertyKey('');
    setNewPropertyValue('');
  }, [feature]);

  if (!feature && isOpen) {
    return null; // 如果沒有 feature 就不顯示
  }

  const handlePropertyChange = (key: string, value: any) => {
    const updatedProperties = { ...editingProperties, [key]: value };
    setEditingProperties(updatedProperties);
    onPropertyChange(`properties.${key}`, value);
  };

  const handlePropertyDelete = (key: string) => {
    const updatedProperties = { ...editingProperties };
    delete updatedProperties[key];
    setEditingProperties(updatedProperties);
    onPropertyDelete(`properties.${key}`);
  };

  const handleAddProperty = () => {
    if (newPropertyKey.trim() && !editingProperties.hasOwnProperty(newPropertyKey)) {
      handlePropertyChange(newPropertyKey, newPropertyValue);
      setNewPropertyKey('');
      setNewPropertyValue('');
      setShowAddProperty(false);
    }
  };

  const isSystemProperty = (key: string): boolean => {
    return key.startsWith('_') || key === 'id';
  };

  const renderPropertyValue = (key: string, value: any): JSX.Element => {
    const isSystem = isSystemProperty(key);
    const isEditable = !readonly && !isSystem;

    // 特殊處理 category 欄位 - 使用可搜尋的下拉選單
    if (key === 'category' && isEditable) {
      return (
        <CategorySelect
          featureType={featureType}
          value={String(value || '')}
          onChange={(newValue) => handlePropertyChange(key, newValue)}
          disabled={!isEditable}
        />
      );
    }

    if (typeof value === 'object' && value !== null) {
      try {
        const jsonString = JSON.stringify(value, null, 2);
        return (
          <textarea
            value={jsonString}
            onChange={(e) => {
              if (isEditable) {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handlePropertyChange(key, parsed);
                } catch {
                  // 暫時保存無效的JSON字符串，等待用戶修正
                  handlePropertyChange(key, e.target.value);
                }
              }
            }}
            disabled={!isEditable}
            className={`w-full p-2 border rounded font-mono text-sm ${
              isEditable
                ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
                : 'border-gray-200 bg-gray-50'
            }`}
            rows={Math.min(jsonString.split('\n').length, 6)}
          />
        );
      } catch {
        return <span className="text-red-500 italic">無效的 JSON 值</span>;
      }
    }

    if (typeof value === 'boolean') {
      return (
        <select
          value={value.toString()}
          onChange={(e) => isEditable && handlePropertyChange(key, e.target.value === 'true')}
          disabled={!isEditable}
          className={`w-full p-2 border rounded ${
            isEditable
              ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
              : 'border-gray-200 bg-gray-50'
          }`}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    if (typeof value === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => isEditable && handlePropertyChange(key, parseFloat(e.target.value) || 0)}
          disabled={!isEditable}
          className={`w-full p-2 border rounded ${
            isEditable
              ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
              : 'border-gray-200 bg-gray-50'
          }`}
        />
      );
    }

    // 字符串或其他類型
    return (
      <input
        type="text"
        value={String(value || '')}
        onChange={(e) => isEditable && handlePropertyChange(key, e.target.value)}
        disabled={!isEditable}
        className={`w-full p-2 border rounded ${
          isEditable
            ? 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500'
            : 'border-gray-200 bg-gray-50'
        }`}
      />
    );
  };

  // 獲取feature的類型
  const getFeatureType = (feature: GeoJSON.Feature): string => {
    const fileType = feature.properties?._file_type;
    const sourceFile = feature.properties?._sourceFile;

    if (fileType) return fileType;
    if (sourceFile) {
      // 從檔案路徑推斷類型，只取檔案名部分
      const fileName = sourceFile.split('/').pop() || sourceFile;
      return fileName.replace('.geojson', '').toLowerCase();
    }
    return 'unknown';
  };

  const getFeatureDisplayName = (feature: GeoJSON.Feature): string => {
    const props = feature.properties;
    if (!props) return "未知項目";

    // 處理 name 屬性，它可能是字串或多語言物件
    if (props.name) {
      if (typeof props.name === "string") {
        return props.name;
      }

      if (typeof props.name === "object" && props.name !== null) {
        const nameValue =
          props.name.en ||
          props.name.zh ||
          props.name["zh-Hant"] ||
          props.name["zh-TW"] ||
          Object.values(props.name)[0];
        if (nameValue && typeof nameValue === "string") {
          return nameValue;
        }
      }
    }

    return String(props.id || "未命名項目");
  };

  if (!feature) return null;

  const featureType = getFeatureType(feature);
  const featureName = getFeatureDisplayName(feature);

  // 獲取相關的 unit/anchor 信息
  const unitIds = feature.properties?.unit_ids;
  const anchorId = feature.properties?.anchor_id;

  return (
    <>
      {/* 背景遮罩 */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 transition-opacity duration-300"
          style={{ zIndex: 10002 }}
          onClick={onClose}
        />
      )}

      {/* 浮動編輯面板 */}
      <div
        className={`fixed right-0 top-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 10003 }}
      >
        {/* 標題列與關閉按鈕 */}
        <div className="p-4 border-b flex justify-between items-center flex-shrink-0">
          <div className="flex-1 mr-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              編輯: {featureName}
            </h3>
            <p className="text-xs text-gray-600">
              {featureType === 'amenity' ? 'Amenity' : featureType === 'occupant' ? 'Occupant' : featureType}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            title="關閉編輯器"
          >
            <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* 捲動內容區 */}
        <div className="flex-1 overflow-y-auto">
          {/* 基本資訊區域（唯讀） - 緊湊行距 */}
          <div className="p-3 border-b bg-gray-50">
            <h4 className="text-sm font-medium text-gray-700 mb-2">📍 基本資訊</h4>
            <div className="space-y-1 text-xs">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">類型:</span>
                <span className="col-span-2 font-mono">{featureType}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <span className="text-gray-500">ID:</span>
                <span className="col-span-2 font-mono text-xs break-all">
                  {feature.id || feature.properties?.id}
                </span>
              </div>
              {featureType === 'amenity' && unitIds && Array.isArray(unitIds) && unitIds.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-gray-500">關聯Units:</span>
                  <span className="col-span-2 font-mono text-green-600">
                    {unitIds.length}個
                  </span>
                </div>
              )}
              {featureType === 'occupant' && anchorId && (
                <div className="grid grid-cols-3 gap-2">
                  <span className="text-gray-500">關聯Anchor:</span>
                  <span className="col-span-2 text-blue-600">✓</span>
                </div>
              )}
            </div>
          </div>

          {/* 可編輯屬性區域 */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">✏️ Properties</h4>

            {/* 人工驗證欄位 - 預先建立 */}
            {!readonly && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  人工驗證欄位
                </h5>

                {/* stoConfirm 欄位 */}
                <div className="mb-3">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <input
                      type="checkbox"
                      checked={editingProperties.stoConfirm === true}
                      onChange={(e) => handlePropertyChange('stoConfirm', e.target.checked)}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    stoConfirm（已確認驗證）
                  </label>
                  <p className="text-xs text-gray-500 ml-6">勾選表示此項目已完成人工驗證</p>
                </div>

                {/* confirmURL 欄位 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    confirmURL（驗證來源 URL）
                  </label>
                  <input
                    type="url"
                    value={editingProperties.confirmURL || ''}
                    onChange={(e) => handlePropertyChange('confirmURL', e.target.value)}
                    placeholder="https://example.com/source"
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">提供驗證相關的 URL 連結</p>
                </div>
              </div>
            )}

            {/* 其他屬性 */}
            <div className="space-y-3">
              {Object.entries(editingProperties)
                .filter(([key]) => !isSystemProperty(key) && key !== 'confirmURL' && key !== 'stoConfirm')
                .map(([key, value]) => (
                  <div key={key} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {key}
                      </label>
                      {!readonly && (
                        <button
                          onClick={() => handlePropertyDelete(key)}
                          className="text-red-600 hover:text-red-800 text-sm"
                          title="刪除這個屬性"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                    {renderPropertyValue(key, value)}
                  </div>
                ))}

              {/* 新增屬性區域 */}
              {!readonly && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
                  {!showAddProperty ? (
                    <button
                      onClick={() => setShowAddProperty(true)}
                      className="w-full text-left text-gray-500 hover:text-gray-700 flex items-center text-sm"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      新增屬性
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="屬性名稱"
                        value={newPropertyKey}
                        onChange={(e) => setNewPropertyKey(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="屬性值"
                        value={newPropertyValue}
                        onChange={(e) => setNewPropertyValue(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleAddProperty}
                          disabled={!newPropertyKey.trim()}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          確認
                        </button>
                        <button
                          onClick={() => {
                            setShowAddProperty(false);
                            setNewPropertyKey('');
                            setNewPropertyValue('');
                          }}
                          className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
                        >
                          取消
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 固定底部按鈕 */}
        {!readonly && (
          <div className="border-t bg-white p-4 flex-shrink-0">
            {pendingChangesCount > 0 && (
              <div className="mb-2 text-xs text-orange-600 flex items-center justify-center">
                <span className="inline-block w-2 h-2 bg-orange-400 rounded-full mr-2"></span>
                {pendingChangesCount} 個待保存更改
              </div>
            )}
            <button
              onClick={onSaveChanges}
              disabled={isSaving || pendingChangesCount === 0}
              className="w-full py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  保存中...
                </>
              ) : (
                `保存更改${pendingChangesCount > 0 ? ` (${pendingChangesCount})` : ''}`
              )}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingEditor;
